import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { Category, IssueFilterKeys, IssueSource } from '../../../../shared/issuesV2';
import * as IssuesV2Store from '../../../redux/issuesV2';
import { useSetFilterHasChanged } from '../../../redux/savedFilters/hooks';
import { FilterPanel } from '../../generic/Filter';
import FilterCardCheckboxes from '../../generic/Filter/FilterCardCheckboxes';
import useFetchIssuesAction from '../IssuesInbox/hooks/useFetchIssuesAction';
import type { SelectOption } from '../../../types';

const DETECTION_SOURCE_DEPENDENCY_OPTION = { value: IssueSource.Dependency, label: 'Dependency' };
const DETECTION_SOURCE_SNIPPET_OPTION = { value: IssueSource.Snippet, label: 'Snippet' };

/**
 * Base issue source options when vendored dependency detection is disabled.
 * Shows "Dependency" and "Snippet" options.
 */
const DETECTION_SOURCE_OPTIONS: SelectOption[] = [
  DETECTION_SOURCE_DEPENDENCY_OPTION,
  DETECTION_SOURCE_SNIPPET_OPTION,
];

/**
 * Dependency source options when vendored dependency detection is enabled.
 * Replaces "Dependency" with "Managed Dependency" and "Vendored Dependency".
 */
const DEPENDENCY_SOURCE_OPTIONS: SelectOption[] = [
  { value: IssueSource.ManagedDependency, label: 'Managed Dependency' },
  { value: IssueSource.VendoredDependency, label: 'Vendored Dependency' },
];

/**
 * Build the issue source options based on whether vendored deps feature is enabled.
 * - When enabled: Managed Dependency, Vendored Dependency, Snippet (for non-Quality)
 * - When disabled: Dependency, Snippet
 */
function getIssueSourceOptions(
  isVendoredDepsEnabled: boolean,
  issueCategory: Category,
): SelectOption[] {
  if (!isVendoredDepsEnabled) {
    // Quality category doesn't have Snippet option
    if (issueCategory === Category.Quality) {
      // when vendored deps feature is disabled then this filter component should not
      // be displayed for Quality issues to begin with, but add a default anyway
      return [DETECTION_SOURCE_DEPENDENCY_OPTION];
    }
    return DETECTION_SOURCE_OPTIONS;
  }

  // Vendored deps enabled - show managed/vendored options
  // Quality category doesn't have Snippet option
  if (issueCategory === Category.Quality) {
    return DEPENDENCY_SOURCE_OPTIONS;
  }

  // For Licensing/Vulnerability, include Snippet option
  return [
    ...DEPENDENCY_SOURCE_OPTIONS,
    DETECTION_SOURCE_SNIPPET_OPTION,
  ];
}

type Props = {
  selectedOptions?: string[];
  isVendoredDepsEnabled: boolean;
  issueCategory: Category;
};

/**
 * Multi-checkbox filter for issue source.
 * When vendored dependency detection is enabled, shows Managed Dependency and Vendored Dependency
 * options instead of the single Dependency option.
 */
const IssueSourceFilter: VoidFunctionComponent<Props> = ({
  selectedOptions,
  isVendoredDepsEnabled,
  issueCategory,
}) => {
  const setFilters = useFetchIssuesAction(IssuesV2Store.actions.setFilter);
  const stateFilters = useSelector(IssuesV2Store.selectors.getFilter);
  const setFilterHasChanged = useSetFilterHasChanged();

  const options = getIssueSourceOptions(isVendoredDepsEnabled, issueCategory);

  const selectedCheckboxes = (selectedOptions ?? []).map((option: string) => ({
    label: options.find(filterOption => filterOption.value === option)?.label || option,
    value: option,
  }));

  const onIssueSourceChange = (newSelected: SelectOption[]) => {
    setFilters({
      ...stateFilters,
      [IssueFilterKeys.IssueSource]: newSelected.map(({ value }) => value as IssueSource),
    });
    setFilterHasChanged(true);
  };

  return (
    <FilterPanel>
      <FilterCardCheckboxes
        label="Issue Source"
        options={options}
        values={selectedCheckboxes}
        onChange={onIssueSourceChange}
        testId="issues-source-filters"
      />
    </FilterPanel>
  );
};

export default IssueSourceFilter;
